import React, { useState, useEffect, useMemo } from 'react';

import i18n from 'i18n-react';
import { storageKeys } from 'config/settings.json';

import Diploma from 'components/Diploma';
import Modal from 'components/Modal';
import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';

import badges from 'assets/images/badges';
import { legacyRemCss } from 'helpers/legacyRem';

import BadgeDisplay from 'components/BadgeDisplay';
import { ContentHeader } from 'components/ContentHeader/ContentHeader';
import RewardMenu from 'components/RewardMenu';
import TierCard from 'components/TierCard';
import { TierRewardsBBI } from 'components/TierRewardsBBI/TierRewardsBBI';

import { calculateDiplomaProgress } from 'helpers/calculateDiplomaProgress';
import { isRewardsCountry } from 'helpers/isRewardsCountry';
import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

/**
 * Helper function to check if a feature flag is enabled
 */
const hasFeatureFlag = (featureFlags, flagName) => {
  if (!featureFlags || !Array.isArray(featureFlags)) return false;
  const flag = featureFlags.find((f) => {
    return f.name === flagName;
  });
  return flag ? (flag.is_enabled ?? true) : false;
};

const params = new URLSearchParams(window.location.search);
let mobile = false;
if (params.get('mobile')) {
  mobile = true;
}

const getBadgeImageUrl = (badge) => {
  return `${badge.replace('https://s3.amazonaws.com/developertool', 'https://travprocdn.imgix.net/')}?w=450&fit=clamp`;
};

/**
 * BadgeView
 *
 * A view component that displays the diploma and each attained badge for the user. These badges being:
 *
 * - Bronze/Silver/Gold/Platinum badges: Based on the amount of completed trainings
 * - Specialization badges
 * - Partner Badges
 *
 * @returns {JSX.Element} A component that displays the user's badges and progress.
 */
const BadgeView = () => {
  const [chapters, setChapters] = useState([]);
  const [subApps, setSubApps] = useState([]);
  const [totalProgressValue, setTotalProgressValue] = useState(0);
  const [showDiploma, setShowDiploma] = useState(false);
  const [destinationName, setDestinationName] = useState('');

  const {
    appId,
    lang: { langFile },
    loaded: settingsLoaded,
    appInfo: { featureFlags },
    salesCompanionAppId,
  } = useSettings();
  const {
    user: { first_name: firstName, last_name: lastName, email, country: userCountry },
    loaded: userInfoLoaded,
  } = useUserInfo();
  const {
    tiers,
    completedItems,
    totalPoints,
    getTieredValue,
    loaded: loadedPointsHandler,
    // New tier system fields
    currentTier,
    tierCopy,
    tierExpiresAt,
    daysUntilExpiry,
    progress,
  } = usePointsHandler();

  const [isRewardsEligible, setIsRewardsEligible] = useState(false);
  useEffect(() => {
    isRewardsCountry(userCountry).then(setIsRewardsEligible);
  }, [userCountry]);

  const hasPointsBasedRewards = useMemo(() => {
    return isRewardsEligible && hasFeatureFlag(featureFlags, 'points_based_rewards');
  }, [featureFlags, isRewardsEligible]);

  const hasMetallicTiers = useMemo(() => {
    return isRewardsEligible && hasFeatureFlag(featureFlags, 'metallic_tiers');
  }, [featureFlags, isRewardsEligible]);
  // Use appropriate tier badges based on tier count
  const { tierBadges, tierBadgesWithPlatinum } = badges;
  const activeTierBadges = tiers && tiers.length === 4 ? tierBadgesWithPlatinum : tierBadges;
  // If currentTier is 'none', user is unranked - set tierIndex to null
  const tierIndex = currentTier === 'none' ? null : tiers && Math.min(getTieredValue(), tiers.length - 1);
  const currentPath = window.location.pathname;
  const hasCompletedDiploma = totalProgressValue >= 100;
  const diplomaImage =
    salesCompanionAppId && email
      ? `https://cms.travpromobile.com/api/app/v2/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${salesCompanionAppId}&email=${email}`
      : '';

  const handleDiplomaOpen = () => {
    return setShowDiploma(true);
  };
  const handleDiplomaClose = () => {
    return setShowDiploma(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const appInfo = await StorageHandler.load(storageKeys.appInfo);
      if (appInfo) {
        setDestinationName(appInfo.appName);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const getUserInfo = () => {
      StorageHandler.load(storageKeys.user).then((res) => {
        if (res) {
          if (!appId || !email) return;
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            if (userProgress) {
              setTotalProgressValue(calculateDiplomaProgress(userProgress.data?.[0]?.chapters));
            }
          });
        } else {
          setTotalProgressValue(0);
        }
      });
    };

    const getSubAppData = (appIds) => {
      if (appIds.length > 0) {
        return APIHandler.getUserProgress(appIds, email).then((userProgress) => {
          if (userProgress) {
            const progress = Object.values(userProgress.data).map((app) => {
              const [chapter] = app.chapters;
              const { appIcon, name, badge } = app;
              return {
                chapter,
                name,
                appIcon,
                badge,
              };
            });
            return progress;
          }
        });
      }
    };

    const getSubApps = async () => {
      const lang = `${langFile}_${langFile.replace('pt', 'br').replace('ko', 'kr').toUpperCase()}`;
      const result = await APIHandler.getSubappsNew(import.meta.env.VITE_APP_ID, email, lang);
      const { categories } = result ?? {};
      if (!categories) return;
      const ids = categories
        .map((cat) => {
          return cat.micro_modules.map(({ session_app_id: sessionAppId }) => {
            return sessionAppId;
          });
        })
        .flat();
      const data = await getSubAppData(ids);
      if (data) {
        setSubApps(data);
      }
    };

    const getChaptersAndProgress = () => {
      APIHandler.getChaptersAndProgress(appId, email, langFile).then((data) => {
        setChapters(data.chapters.uncategorized[0].chapters);
      });
    };

    if (settingsLoaded && userInfoLoaded) {
      getChaptersAndProgress();
      getSubApps();
      getUserInfo();
    }
  }, [appId, email, langFile, settingsLoaded, userInfoLoaded]);

  return (
    <div className="contentContainer badgeView">
      {currentPath === '/main/rewards/achievements' && <RewardMenu />}
      <ContentHeader
        header={i18n.translate('badgeView.title')}
        subtitle={i18n.translate(hasMetallicTiers ? 'badgeView.subtitle' : 'badgeView.subtitleNoMetallic', {
          program_name_short: destinationName,
        })}
        pageHeader
      />

      <hr />

      <div className="content-box">
        {/* New Points-Based Rewards Tier System */}
        {loadedPointsHandler && tiers && hasPointsBasedRewards && (
          <div className="badgeContainer points__container tier-system">
            <TierCard
              tiers={tiers}
              currentTier={currentTier}
              tierCopy={tierCopy}
              tierExpiresAt={tierExpiresAt}
              daysUntilExpiry={daysUntilExpiry}
              progress={progress}
            />
          </div>
        )}

        {/* Legacy Metallic Tiers Display (when points_based_rewards is NOT enabled) */}
        {loadedPointsHandler && tiers && !hasPointsBasedRewards && hasMetallicTiers && (
          <div className="badgeContainer points__container">
            <ContentHeader header={i18n.translate('badgeView.badgeDisplayTitle')} />
            <BadgeDisplay
              tierBadges={activeTierBadges}
              tiers={tiers}
              tierIndex={tierIndex}
              totalPoints={totalPoints}
              layout="longBar"
              hideBadge
              completedItems={completedItems}
              hasPopout
            />
            <div className={`badgeView__title--tiers ${tiers.length === 4 ? 'four' : 'three'}`}>
              {tiers.map((tier, index) => {
                return (
                  <div
                    key={tier.title}
                    className={`tier-title ${
                      tierIndex === index ? 'active' : ''
                    } ${tier.completedItems >= tier.count ? '' : 'disabled'}`}
                  >
                    <img className="badge" src={activeTierBadges[index]} alt="Logo" />

                    <span className="title">
                      {Math.min(tier.completedItems, tier.count)}
                      {' / '}
                      {tier.count} {i18n.translate('badgeView.complete')}
                    </span>
                  </div>
                );
              })}
            </div>
            {Number(appId) === 940 && (
              <TierRewardsBBI
                tiers={tiers}
                tierIndex={tierIndex}
                totalProgressValue={totalProgressValue}
                loadedPointsHandler={loadedPointsHandler}
                mobile={mobile}
              />
            )}
            <hr />
          </div>
        )}

        <div>
          <ContentHeader header={i18n.translate('badgeView.diploma') || 'Your Diploma'} />
          <button
            className={`diploma__container ${hasCompletedDiploma ? '' : 'disabled'}`}
            type="button"
            disabled={!hasCompletedDiploma}
            onClick={handleDiplomaOpen}
          >
            <img className="diploma__image" src={diplomaImage} alt="diploma" />
          </button>
          {hasCompletedDiploma && (
            <Modal show={showDiploma} onHide={handleDiplomaClose} bgClose autosize>
              <Diploma appId={appId} firstName={firstName} lastName={lastName} email={email} key={firstName} />
            </Modal>
          )}
        </div>
        {chapters.filter((chapter) => {
          return chapter.badge !== null;
        }).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.chapterBadges')} />
            <div className="badges__collection">
              {chapters.map((chapter) => {
                const { id, percentage, badge, name, chapterTitle } = chapter;
                if (!badge) {
                  return null;
                }

                const filename = `${name || chapterTitle || 'badge'}`;
                const isCompleted = percentage === 100;
                const badgeLabel = name || chapterTitle || 'Chapter badge';
                const badgeStyle = {
                  backgroundImage: `url(${getBadgeImageUrl(badge)})`,
                };

                if (!isCompleted) {
                  return (
                    <button
                      key={id}
                      aria-label={badgeLabel}
                      className="chapter-badge disabled"
                      type="button"
                      disabled
                      style={badgeStyle}
                    />
                  );
                }

                return (
                  <a
                    key={id}
                    aria-label={badgeLabel}
                    className="chapter-badge"
                    href={APIHandler.getDownloadURL(badge, filename)}
                    style={badgeStyle}
                  />
                );
              })}
            </div>
          </div>
        )}
        {subApps.filter((subapp) => {
          return subapp.badge !== null;
        }).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.partnerBadges')} />
            <div className="badges__collection">
              {subApps.map((subapp) => {
                const {
                  name,
                  chapter: { progress, total_pages: totalPages },
                  badge,
                } = subapp;
                if (!badge) {
                  return null;
                }

                const filename = `${name || 'badge'}.png`;
                const isCompleted = progress >= totalPages - 1;
                const badgeKey = `${name || badge}-${totalPages}`;
                const badgeLabel = name || 'Partner badge';
                const badgeStyle = {
                  backgroundImage: `url(${getBadgeImageUrl(badge)})`,
                };

                if (!isCompleted) {
                  return (
                    <button
                      key={badgeKey}
                      aria-label={badgeLabel}
                      className="subapp-badge disabled"
                      type="button"
                      disabled
                      style={badgeStyle}
                    />
                  );
                }

                return (
                  <a
                    key={badgeKey}
                    aria-label={badgeLabel}
                    className="subapp-badge"
                    href={APIHandler.getDownloadURL(badge, filename)}
                    style={badgeStyle}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      {mobile && (
        <style>
          {`.buttons__diploma { display: none !important; } .sharebox__buttons { display: none !important; } .contentContainer { margin-top: ${legacyRemCss(5)}; } .header { display: none; } .cookieConsent { display: none; }`}
        </style>
      )}
    </div>
  );
};

export default BadgeView;
